import React, { useState, useEffect, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import { motion } from "framer-motion";
import { allVideos } from "./data/videos";
import { clientAssignments } from "./data/videos";
import useAuth from "../hooks/useAuth";
import DataTable from 'react-data-table-component';
import {
  PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis,
  LineChart, Line, Legend,
} from 'recharts';
import './PatientDashboard.css';
import api from './api';
// Doctors.css removed — its global selectors were overriding inline styles
import EscalationModal from './EscalationModal';
import { RomAssessmentPanel } from './RomAssessment';
import PatientAIReport from './PatientAIReport';
import { AllRomPartsProgress } from './PatientProgressModal';

const COLORS = ['#0088FE', '#FF8042', '#00C49F', '#FFBB28', '#845EC2', '#D65DB1'];

// ── Light DataTable theme ─────────────────────────────────────────────────────
const lightTable = {
  table:      { style: { background: 'transparent' } },
  headRow:    { style: { background: 'rgba(25,118,210,0.06)', borderBottomColor: 'rgba(25,118,210,0.14)', minHeight: '42px' } },
  headCells:  { style: { color: '#1976d2', fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', paddingLeft: '12px' } },
  rows:       { style: { background: 'transparent', borderBottomColor: 'rgba(0,0,0,0.06)', minHeight: '46px' } },
  cells:      { style: { color: '#2d3436', fontSize: '0.83rem', paddingLeft: '12px' } },
  pagination: { style: { background: 'transparent', color: '#1976d2', borderTopColor: 'rgba(0,0,0,0.08)', fontSize: '0.8rem' } },
  expanderRow:    { style: { background: '#f7f9fc' } },
  expanderButton: { style: { color: '#1976d2' } },
  noData:     { style: { background: 'transparent', color: '#9ca3af' } },
};

// ── Expandable row components ─────────────────────────────────────────────────
const PdBillExpandRow = ({ data }) => (
  <div style={{ padding: '12px 20px', background: '#f7f9fc', borderBottom: '1px solid #e8edf5' }}>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px', marginBottom: 8 }}>
      <div><span style={{ color: '#888', fontSize: 12 }}>Bill ID</span><br /><strong>{data.invoicenumber}</strong></div>
      <div><span style={{ color: '#888', fontSize: 12 }}>Invoice Date</span><br /><strong>{new Date(data.invoicedate).toLocaleDateString('en-GB')}</strong></div>
      <div><span style={{ color: '#888', fontSize: 12 }}>Payment Mode</span><br /><strong>{data.paymentmode}</strong></div>
      <div><span style={{ color: '#888', fontSize: 12 }}>Cashier</span><br /><strong>{data.cashier || '—'}</strong></div>
      <div><span style={{ color: '#888', fontSize: 12 }}>Total</span><br /><strong>₹{data.total}</strong></div>
      <div><span style={{ color: '#888', fontSize: 12 }}>Given Back</span><br /><strong>₹{data.givenback}</strong></div>
    </div>
    <div>
      <strong style={{ fontSize: 12 }}>Services</strong>
      <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 13 }}>
        {data.services.map((svc, i) => (
          <li key={i}>{svc.name} — Qty: {svc.qty} | ₹{svc.amount}{svc.sdiscount ? ' (Disc: ' + svc.sdiscount + ')' : ''}</li>
        ))}
      </ul>
    </div>
  </div>
);

const PdSessionExpandRow = ({ data }) => (
  <div style={{ padding: '10px 20px', background: '#f7f9fc', borderBottom: '1px solid #e8edf5', fontSize: 13, color: '#555' }}>
    {data.sessionComments       && <div><strong>Comments:</strong> {data.sessionComments}</div>}
    {data.helperTherapist1      && <div><strong>Therapist 1:</strong> {data.helperTherapist1}</div>}
    {data.helperTherapist2      && <div><strong>Therapist 2:</strong> {data.helperTherapist2}</div>}
    {data.leadTherapistDesignation && <div><strong>Designation:</strong> {data.leadTherapistDesignation}</div>}
  </div>
);

function accumulateData(bills) {
  let totalOutstanding = 0, totalBills = 0;
  let paymentModeCount = {}, orgSpent = {}, serviceStats = {};
  let dateCounts = {}, bundleCounts = [];

  bills.forEach(b => {
    totalOutstanding += b.balance;
    totalBills++;

    paymentModeCount[b.paymentmode] = (paymentModeCount[b.paymentmode] || 0) + 1;
    orgSpent[b.billorg] = (orgSpent[b.billorg] || 0) + b.total;

    const svcNames = b.services.map(s => s.name).sort();
    svcNames.forEach((name, idx) => {
      serviceStats[name] = serviceStats[name] || { qty: 0, count: 0 };
      serviceStats[name].qty += b.services[idx].qty;
      serviceStats[name].count++;
    });

    for (let i = 0; i < svcNames.length - 1; i++) {
      const combo = svcNames[i] + ' + ' + svcNames[i + 1];
      bundleCounts[combo] = (bundleCounts[combo] || 0) + 1;
    }

    const d = new Date(b.invoicedate).toISOString().split('T')[0];
    dateCounts[d] = (dateCounts[d] || 0) + 1;
  });

  const pieData = Object.entries(paymentModeCount).map(([name, value]) => ({ name, value }));
  const barOrgData = Object.entries(orgSpent).map(([org, spend]) => ({ org, spend }));
  const serviceData = Object.entries(serviceStats).map(([name, s]) => ({ name, qty: s.qty }));
  const lineData = Object.entries(dateCounts).map(([date, visits]) => ({ date, visits }));
  const bundleData = Object.entries(bundleCounts).map(([combo, count]) => ({ combo, count }));

  const avgSpend = bills.length
    ? (bills.reduce((sum, b) => sum + b.total, 0) / bills.length).toFixed(2)
    : 0;

  return {
    totalOutstanding,
    totalBills,
    pieData,
    barOrgData,
    serviceData,
    lineData,
    bundleData,
    avgSpend
  };
}
const BillDetails = ({ data }) => (
  <>
  <div
      style={{
        display: 'flex', width:  '70vw', padding: '20px', background: data.balance > 0 ? '#f2cad0' : '#cbf2cb'
      }}
    >

  <div style={{ display: 'grid' , gridTemplateColumns: '0.5fr 0.5fr', 
                gap: '8px 20px', width: '22vw', marginLeft: '64px' }}>
      <strong>Bill ID:</strong> {data.invoicenumber} <br />
      <strong>Patient Card:</strong> {data.patientCard} <br />
      <strong>Invoice Date:</strong> {new Date(data.invoicedate).toLocaleDateString()} <br />
      <strong>Payment Mode:</strong> {data.paymentmode} <br />
    </div>

     <div style={{ display: 'grid' , gridTemplateColumns: '0.5fr 0.5fr', 
                gap: '8px 20px', width: '22vw', marginLeft: '64px' }}>
      <strong>Total Amount:</strong> ₹{data.total} <br />
      <strong>Amount Given Back:</strong> ₹{data.givenback} <br />  
      <strong>Balance:</strong> ₹{data.balance} <br />
      <strong>Cashier:</strong> {data?.cashier} <br />
    </div>
</div>

  <div style={{ padding: '10px 20px', marginLeft: '64px', background: data.balance > 0 ? '#f2cad0' : '#cbf2cb' }}>
    <strong>Services:</strong>
    <ul style={{ padding: 0, margin: 0 }}>
      {data.services.map((svc, i) => (
        <li  style={{
        display: 'block',         // ensures it takes up full line
        marginBottom: '4px',      // optional spacing between items
        listStyleType: 'disc',    // or 'none' if you don’t want bullets
        marginLeft: '20px'        // indent bullets
      }}
      key={i}>{svc.name} :-: Qty: {svc.qty} | Amount: {svc.amount} | Discount : {svc.sdiscount || 0} </li>        
      ))}
    </ul>
  
    {/* Add more details as you want */}
  </div>
  </>
);

const comments = ({ data }) => (
  <>
  <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 0.5fr',
                gap: '8px 20px', width: '30vw', marginLeft: '64px', padding: '10px 0' }}>
    <strong>Comments:</strong> <span>{data.sessionComments || '—'}</span>
    <strong>Lead Therapist:</strong> <span>{data?.leadTherapist || '—'}</span>
    <strong>Support Therapist 1:</strong> <span>{data?.helperTherapist1 || '—'}</span>
    <strong>Support Therapist 2:</strong> <span>{data?.helperTherapist2 || '—'}</span>
    <strong>Designation:</strong> <span>{data?.leadTherapistDesignation || '—'}</span>
    <strong>Visit Charges:</strong> <span style={{ fontWeight: 700, color: '#1976d2' }}>₹{data?.visitCharges || 0}</span>
  </div>
  </>
);

const getYoutubeId = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    return u.searchParams.get("v");
  } catch {
    return null;
  }
};

// ── ROM & Muscle chart helpers ─────────────────────────────────────────────────
// Returns bar-chart data: flexion active (L/R) vs normal per body part
function buildRomChart(rom) {
  const parts = [
    { key: 'cervical',  label: 'Cervical',  mv: 'flexion',      normal: 45,  bi: false },
    { key: 'shoulder',  label: 'Shoulder',  mv: 'flexion',      normal: 180, bi: true  },
    { key: 'elbow',     label: 'Elbow',     mv: 'flexion',      normal: 145, bi: true  },
    { key: 'wrist',     label: 'Wrist',     mv: 'flexion',      normal: 80,  bi: true  },
    { key: 'lowerBack', label: 'L. Back',   mv: 'flexion',      normal: 90,  bi: false },
    { key: 'hip',       label: 'Hip',       mv: 'flexion',      normal: 120, bi: true  },
    { key: 'knee',      label: 'Knee',      mv: 'flexion',      normal: 135, bi: true  },
    { key: 'ankle',     label: 'Ankle',     mv: 'dorsiflexion', normal: 20,  bi: true  },
  ];
  return parts.map(bp => {
    const m = rom?.[bp.key]?.[bp.mv] || {};
    const l = bp.bi ? m.left?.active  : m.active;
    const r = bp.bi ? m.right?.active : undefined;
    if (l == null && r == null) return null;
    return { name: bp.label, Normal: bp.normal, 'Left Active': l, 'Right Active': r };
  }).filter(Boolean);
}

// Returns bar-chart data: primary muscle MVC grade per body part
function buildMuscleChart(ms) {
  const parts = [
    { key: 'cervical',  label: 'Cervical', grp: 'flexors',      bi: false },
    { key: 'shoulder',  label: 'Shoulder', grp: 'flexors',      bi: true  },
    { key: 'elbow',     label: 'Elbow',    grp: 'flexors',      bi: true  },
    { key: 'wrist',     label: 'Wrist',    grp: 'flexors',      bi: true  },
    { key: 'lowerBack', label: 'L. Back',  grp: 'extensors',    bi: false },
    { key: 'hip',       label: 'Hip',      grp: 'flexors',      bi: true  },
    { key: 'knee',      label: 'Knee',     grp: 'extensors',    bi: true  },
    { key: 'ankle',     label: 'Ankle',    grp: 'dorsiflexors', bi: true  },
  ];
  return parts.map(bp => {
    const g = ms?.[bp.key]?.[bp.grp] || {};
    const l = bp.bi ? (g.left?.grade  != null ? Number(g.left.grade)  : null) : (g.grade != null ? Number(g.grade) : null);
    const r = bp.bi ? (g.right?.grade != null ? Number(g.right.grade) : null) : null;
    if (l == null && r == null) return null;
    return { name: bp.label, Left: l, Right: r };
  }).filter(Boolean);
}

// Returns summary rows for the quick-view assessment table
function buildAssessmentRows(rom, ms) {
  const parts = [
    { key: 'cervical',  label: 'Cervical (Neck)',          mvF: 'flexion',      mvE: 'extension',    mGrp: 'flexors',      bi: false, normF: 45,  normE: 45  },
    { key: 'shoulder',  label: 'Shoulder',                 mvF: 'flexion',      mvE: 'extension',    mGrp: 'flexors',      bi: true,  normF: 180, normE: 60  },
    { key: 'elbow',     label: 'Elbow / Forearm',          mvF: 'flexion',      mvE: 'extension',    mGrp: 'flexors',      bi: true,  normF: 145, normE: 0   },
    { key: 'wrist',     label: 'Wrist',                    mvF: 'flexion',      mvE: 'extension',    mGrp: 'flexors',      bi: true,  normF: 80,  normE: 70  },
    { key: 'lowerBack', label: 'Lumbar (Lower Back)',       mvF: 'flexion',      mvE: 'extension',    mGrp: 'extensors',    bi: false, normF: 90,  normE: 30  },
    { key: 'hip',       label: 'Hip',                      mvF: 'flexion',      mvE: 'extension',    mGrp: 'flexors',      bi: true,  normF: 120, normE: 30  },
    { key: 'knee',      label: 'Knee',                     mvF: 'flexion',      mvE: 'extension',    mGrp: 'extensors',    bi: true,  normF: 135, normE: 0   },
    { key: 'ankle',     label: 'Ankle / Foot',             mvF: 'dorsiflexion', mvE: 'plantarFlexion', mGrp: 'dorsiflexors', bi: true, normF: 20, normE: 50 },
  ];
  return parts.map(bp => {
    const rd = rom?.[bp.key] || {};
    const msd = ms?.[bp.key]?.[bp.mGrp] || {};
    const fl = bp.bi ? rd[bp.mvF]?.left?.active  : rd[bp.mvF]?.active;
    const fr = bp.bi ? rd[bp.mvF]?.right?.active : null;
    const el = bp.bi ? rd[bp.mvE]?.left?.active  : rd[bp.mvE]?.active;
    const er = bp.bi ? rd[bp.mvE]?.right?.active : null;
    const ml = bp.bi ? (msd.left?.grade  ?? null) : (msd.grade ?? null);
    const mr = bp.bi ? (msd.right?.grade ?? null) : null;
    const painL = bp.bi
      ? Object.values(rd).some(m => m.left?.pain)
      : Object.values(rd).some(m => m.pain);
    const painR = bp.bi ? Object.values(rd).some(m => m.right?.pain) : false;
    // Only include row if any data exists
    if (fl == null && fr == null && el == null && er == null && ml == null) return null;
    return { ...bp, fl, fr, el, er, ml, mr, painL, painR };
  }).filter(Boolean);
}

// Colour-code a ROM value vs its normal reference
function romColor(val, normal) {
  if (val == null || normal === 0) return '#374151';
  const pct = (val / normal) * 100;
  if (pct >= 90) return '#16a34a';   // green – near-normal
  if (pct >= 60) return '#d97706';   // amber – moderate deficit
  return '#dc2626';                   // red   – significant deficit
}

// MVC grade badge colour
const MVC_BADGE = { '': '#9ca3af', '0': '#c62828', '1': '#e65100', '2': '#f57f17', '3': '#2e7d32', '4': '#1565c0', '5': '#283593' };

export default function PatientDashboard() {
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [profile, setProfile] = useState({});
  const [bills, setBills] = useState([]);
  const [videos, setVideos] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [pdTab, setPdTab] = useState('overview');   // 'overview' | 'sessions' | 'rom' | 'muscle'
  const [romData, setRomData] = useState(null);
  const [romHistory, setRomHistory] = useState([]);
  const [romHistLoading, setRomHistLoading] = useState(false);
const { auth } = useAuth();
  const debounceRef = useRef(null);

// Debounced profile search
  useEffect(() => {
    if (searchInput.length < 3) {
      setSearchResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await api.get(`${process.env.REACT_APP_URL}/patients/profiles/search/${searchInput}`);
        setSearchResults(res.data || []);
      } catch (err) {
        console.error('Error searching profiles:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    const blockKeys = (e) => {
    if (e.ctrlKey && ["s", "u", "i"].includes(e.key.toLowerCase())) {
    e.preventDefault();
    }
    };
    window.addEventListener("keydown", blockKeys);
    return () => window.removeEventListener("keydown", blockKeys);
  }, []);



   const fetchBillsByCard = async (cardId) => {
    setLoading(true);
    setVisible(false);

    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/bills/${cardId}`);
      const data = res.data;
      if (Array.isArray(data) && data.length > 0) {
        setBills(data);
        setVisible(true);
      } else {
        setBills([]);
        setVisible(false);
      }
    } catch (err) {
      console.error('Error fetching bills:', err);
      setBills([]);
      setVisible(false);
      alert('Error fetching bills. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Always fetch a fresh patient profile so previousbalancedetails is authoritative
  const fetchProfile = async (cardId) => {
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/patients/profiles/${cardId}`);
      if (res.data) setProfile(res.data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const fetchSessions = async (cardId) => {

    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/appointments/type/Therapy/patient/${cardId}`);
      const data = res.data;
      if (Array.isArray(data) && data.length > 0) {
        setSessions(data);
        
      } else {
        setSessions([]);
      }
    } catch (err) {
      console.error('Error fetching Sessions:', err);
      setSessions([]);
      alert('Error fetching Sessions. Please try again.');
    } 
  };

  const fetchvideos = async (cardId) => {
  // If you had BE:
  // const res = await api.get(`${process.env.REACT_APP_URL}/videos/${cardId}`);
  // const data = res.data;

  // Example: mock data
  const visibleVideos = allVideos
    .filter((v) => clientAssignments.assignedVideoIds.includes(v.id))
    .map((v) => {
      const youtubeId = getYoutubeId(v.youtubeUrl);

      // ✅ Console log for each video
      console.log("Title:", v.title);
      console.log("Original URL:", v.youtubeUrl);
      console.log("Extracted Video ID:", youtubeId);
      console.log("Final Embed URL:", `https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1&controls=1`);
      console.log("------------");

      return {
        ...v,
        youtubeId
      };
    });

  setVideos(visibleVideos);

};

  const fetchRomData = async (cardId) => {
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/patients/rom/${cardId}`);
      setRomData(res.data || null);
    } catch {
      setRomData(null);
    }
  };

  const fetchRomHistory = async (cardId) => {
    setRomHistory([]); setRomHistLoading(true);
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/patients/rom/${cardId}/history`);
      setRomHistory(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRomHistory([]);
    } finally {
      setRomHistLoading(false);
    }
  };

  const d = visible ? accumulateData(bills) : null;

  // ── FIFO session payment allocation (oldest sessions deducted first) ──────
  const paidSum = bills.reduce((acc, b) => acc + b.paid, 0);
  const _completedSorted = sessions
    .filter(s => s.status === 'Completed' && (s.visitCharges || 0) > 0)
    .slice()
    .sort((a, b) => new Date(a.date || a.start) - new Date(b.date || b.start));
  let _running = paidSum;
  const _allocMap = {};
  _completedSorted.forEach(s => {
    const cost = s.visitCharges || 0;
    _allocMap[s._id] = { paid: _running >= cost };
    _running -= cost;
  });
  const sessionCredit = _running;
  let _lastPaid = null;
  for (let _pi = _completedSorted.length - 1; _pi >= 0; _pi--) {
    if (_allocMap[_completedSorted[_pi]._id].paid) { _lastPaid = _completedSorted[_pi]; break; }
  }
  const firstPending   = _completedSorted.find(s => !_allocMap[s._id].paid);
  const pendingCount   = _completedSorted.filter(s => !_allocMap[s._id].paid).length;
  const coverageEndDt  = _lastPaid    ? new Date(_lastPaid.date    || _lastPaid.start)    : null;
  const pendingStartDt = firstPending ? new Date(firstPending.date || firstPending.start) : null;

  // ── Table columns ──────────────────────────────────────────────────────────
  const pdBillColumns = [
    { name: 'Date',     selector: r => new Date(r.invoicedate), format: r => new Date(r.invoicedate).toLocaleDateString('en-GB'), sortable: true, width: '108px' },
    { name: 'Time',     selector: r => new Date(r.invoicedate).toLocaleTimeString('en-US', { hour12: true }), width: '98px' },
    { name: 'Total',    selector: r => r.total,    sortable: true, right: true, width: '90px',
      cell: r => <span style={{ color: '#1976d2', fontWeight: 700 }}>₹{r.total}</span> },
    { name: 'Discount', selector: r => r.discount, sortable: true, width: '90px' },
    { name: 'Received', selector: r => r.paid,     sortable: true, width: '95px',
      cell: r => <span>₹{r.paid}</span> },
    { name: 'Balance',  selector: r => r.balance,  sortable: true, right: true, width: '95px',
      cell: r => r.balance > 0
        ? <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>₹{r.balance}</span>
        : r.balance < 0
        ? <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>₹{r.balance}</span>
        : <span style={{ background: '#f3f4f6', color: '#6b7280', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>Settled</span>
    },
    { name: 'Org', selector: r => r.billorg, sortable: true },
  ];

  const pdSessionColumns = [
    { name: 'Date',      selector: r => new Date(r.date), format: r => new Date(r.date).toLocaleDateString('en-GB'), sortable: true, width: '108px' },
    { name: 'Start',     selector: r => new Date(r.start).toLocaleTimeString('en-US', { hour12: true }), width: '100px' },
    { name: 'Therapist', selector: r => r.leadTherapist, sortable: true },
    { name: 'Status',    selector: r => r.status, sortable: true, width: '105px',
      cell: r => <span style={{ color: r.status === 'Completed' ? '#2e7d32' : r.status === 'Skipped' ? '#d97706' : '#555', fontWeight: 600, fontSize: 12 }}>{r.status}</span> },
    { name: 'Charges',   selector: r => r.visitCharges, sortable: true, right: true, width: '85px',
      cell: r => <span style={{ color: '#1976d2', fontWeight: 700 }}>₹{r.visitCharges || 0}</span> },
    { name: 'Payment',   sortable: false, right: true, width: '115px',
      cell: r => {
        if (r.status !== 'Completed' || !(r.visitCharges > 0)) return <span style={{ color: '#d1d5db', fontSize: 11 }}>—</span>;
        const a = _allocMap[r._id];
        if (!a) return <span style={{ color: '#d1d5db', fontSize: 11 }}>—</span>;
        return a.paid
          ? <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>✓ Paid</span>
          : <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>✗ ₹{(r.visitCharges || 0).toLocaleString()} due</span>;
      }
    },
  ];

  return (
    <div className="dashboard">
      <header>
        <div className="search-section">
          <label>Search by Name, Card ID, or Phone Number</label>
          <input
            type="text"
            value={searchInput}
            list="patient-options"
            onInput={(e) => {
                const val = e.target.value;
                setSearchInput(val);
                const matchedProfile = searchResults.find(
                  p => `${p.name} (${p.patientCard}) - ${p.number}` === val
                );
                if (matchedProfile) {
                  setSearchInput('');
                  setSearchResults([]);
                  fetchProfile(matchedProfile.patientCard);
                  fetchBillsByCard(matchedProfile.patientCard);
                  fetchSessions(matchedProfile.patientCard);
                  fetchvideos(matchedProfile.patientCard);
                  fetchRomData(matchedProfile.patientCard);
                  fetchRomHistory(matchedProfile.patientCard);
                  setPdTab('overview');
                }
              }}
            placeholder="Type 3+ characters to search..."
          />
          <datalist id="patient-options">
            {[...new Set(
              searchResults.map(
                patient => `${patient.name} (${patient.patientCard}) - ${patient.number}`
              )
            )].map((value, idx) => (
              <option key={idx} value={value} />
            ))}
          </datalist>
          {isSearching && <p>Searching...</p>}
        </div>
      </header>

      {/* ── Patient selected: show tabs ────────────────────────────────────── */}
      {profile?.patientCard && (
        <>
          {/* Patient greeting card */}
          <div style={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
            borderRadius: 16, padding: '20px 24px', marginTop: 16, marginBottom: 12,
            color: '#fff', boxShadow: '0 4px 20px rgba(37,99,235,0.25)',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.7, marginBottom: 4 }}>
              Patient Profile
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.3 }}>
              {profile.name}
            </div>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span>{profile.patientCard}</span>
              <span>{profile.number}</span>
              {profile.therapist && <span>Therapist: {profile.therapist}</span>}
            </div>
          </div>

          {/* Pill tab bar */}
          <div style={{ display: 'flex', gap: 4, background: '#fff', borderRadius: 14, padding: 4, marginBottom: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflowX: 'auto' }}>
            {[
              { key: 'overview',  label: 'Overview'          },
              { key: 'sessions',  label: 'Sessions'          },
              { key: 'bills',     label: 'Bills'             },
              { key: 'rom',       label: 'ROM'               },
              { key: 'muscle',    label: 'Muscle'            },
              { key: 'aiReport',  label: 'AI Report'         },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setPdTab(tab.key)}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: 'none',
                  background: pdTab === tab.key ? 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)' : 'transparent',
                  color: pdTab === tab.key ? '#fff' : '#64748b',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: pdTab === tab.key ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
                  whiteSpace: 'nowrap',
                  fontFamily: "'Inter', system-ui, sans-serif",
                }}
              >{tab.label}</button>
            ))}
          </div>

          {/* ── Overview dashboard tab ── */}
          <div style={{ display: pdTab === 'overview' ? '' : 'none', marginTop: 16 }}>
            {(() => {
              const romChartData    = buildRomChart(romData?.rom);
              const muscleChartData = buildMuscleChart(romData?.muscleStrength);
              const assessRows      = buildAssessmentRows(romData?.rom, romData?.muscleStrength);
              const formalBal = (profile?.previousbalancedetails || []).reduce((s, d) => s + Number(d.balance || 0), 0);
              const billedOutstanding = (profile?.totalSoFar || 0) - (profile?.totalPaidSoFar || 0);
              const unbilledFees = Math.max(0, (profile?.overallVisitCharges || 0) - (profile?.totalSoFar || 0));
              const totalBal = Math.max(formalBal, billedOutstanding) + unbilledFees;
              const bodyPartsAssessed = assessRows.length;
              return (
                <div style={{ fontFamily: "'Inter', sans-serif" }}>

                  {/* ── KPI Cards ── */}
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>
                    {[
                      { icon: '🗓️', label: 'Total Sessions',     value: sessions.length,                   color: '#1976d2' },
                      { icon: '💰', label: 'Outstanding Balance', value: `₹${totalBal}`,                   color: totalBal > 0 ? '#d32f2f' : '#2e7d32' },
                      { icon: '📐', label: 'Body Parts Assessed', value: bodyPartsAssessed || '—',          color: '#7b1fa2' },
                      { icon: '📅', label: 'Last Assessment',     value: romData?.updatedAt ? new Date(romData.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Not done', color: '#00897b' },
                      { icon: '🏃', label: 'Session Type',        value: romData?.assessmentType || '—',   color: '#f57c00' },
                    ].map(c => (
                      <div key={c.label} style={{
                        flex: '1 1 140px', minWidth: 130, background: '#fff', borderRadius: 14,
                        padding: '16px 18px', boxShadow: '0 2px 10px rgba(0,0,0,.07)',
                        borderLeft: `4px solid ${c.color}`,
                      }}>
                        <div style={{ fontSize: 22, marginBottom: 4 }}>{c.icon}</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: c.color, lineHeight: 1.1 }}>{c.value}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginTop: 4, textTransform: 'uppercase', letterSpacing: .6 }}>{c.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* ── Charts row ── */}
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 22 }}>

                    {/* ROM Flexion chart */}
                    <div style={{ flex: '1 1 420px', background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 10px rgba(0,0,0,.07)' }}>
                      <h4 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#1a1d23' }}>📐 ROM – Flexion Active vs Normal (°)</h4>
                      {romChartData.length === 0 ? (
                        <div style={{ color: '#9ca3af', fontSize: 13, padding: '30px 0', textAlign: 'center' }}>No ROM data recorded yet</div>
                      ) : (
                        <BarChart width={420} height={220} data={romChartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} domain={[0, 'auto']} />
                          <Tooltip formatter={(v, n) => [`${v}°`, n]} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="Normal"       fill="#d1d5db" radius={[4,4,0,0]} />
                          <Bar dataKey="Left Active"  fill="#2196f3" radius={[4,4,0,0]} />
                          <Bar dataKey="Right Active" fill="#ef4444" radius={[4,4,0,0]} />
                        </BarChart>
                      )}
                    </div>

                    {/* Muscle Strength chart */}
                    <div style={{ flex: '1 1 340px', background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 10px rgba(0,0,0,.07)' }}>
                      <h4 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#1a1d23' }}>💪 Muscle Strength – MVC Grade (0–5)</h4>
                      {muscleChartData.length === 0 ? (
                        <div style={{ color: '#9ca3af', fontSize: 13, padding: '30px 0', textAlign: 'center' }}>No strength data recorded yet</div>
                      ) : (
                        <BarChart width={340} height={220} data={muscleChartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, 5]} ticks={[0,1,2,3,4,5]} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v, n) => [`Grade ${v}`, n]} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="Left"  fill="#2196f3" radius={[4,4,0,0]} />
                          <Bar dataKey="Right" fill="#ef4444" radius={[4,4,0,0]} />
                        </BarChart>
                      )}
                    </div>
                  </div>

                  {/* ── Assessment Summary Table ── */}
                  {assessRows.length > 0 && (
                    <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 10px rgba(0,0,0,.07)', marginBottom: 22, overflowX: 'auto' }}>
                      <h4 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#1a1d23' }}>📋 Assessment Quick Summary</h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#f7f9fc' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap' }}>Body Part</th>
                            <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 600, color: '#1d4ed8', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap', background: '#eff6ff' }}>Flex L°</th>
                            <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 600, color: '#b91c1c', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap', background: '#fff1f2' }}>Flex R°</th>
                            <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 600, color: '#9ca3af', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap' }}>Norm F°</th>
                            <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 600, color: '#1d4ed8', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap', background: '#eff6ff' }}>Ext L°</th>
                            <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 600, color: '#b91c1c', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap', background: '#fff1f2' }}>Ext R°</th>
                            <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 600, color: '#9ca3af', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap' }}>Norm E°</th>
                            <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 600, color: '#1d4ed8', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap', background: '#eff6ff' }}>Muscle L</th>
                            <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 600, color: '#b91c1c', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap', background: '#fff1f2' }}>Muscle R</th>
                            <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 600, color: '#6b7280', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap' }}>Pain</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assessRows.map((r, i) => {
                            const gradeChip = (g) => g != null ? (
                              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: MVC_BADGE[String(g)] + '22', color: MVC_BADGE[String(g)] }}>
                                {g}/5
                              </span>
                            ) : <span style={{ color: '#d1d5db' }}>—</span>;
                            const degCell = (v, norm) => v != null
                              ? <span style={{ fontWeight: 700, color: romColor(v, norm) }}>{v}°</span>
                              : <span style={{ color: '#d1d5db' }}>—</span>;
                            return (
                              <tr key={r.key} style={{ background: i % 2 ? '#fafbfd' : '#fff' }}>
                                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{r.label}</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center', background: '#f8fbff' }}>{degCell(r.fl, r.normF)}</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center', background: '#fff8f8' }}>{r.bi ? degCell(r.fr, r.normF) : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center', color: '#9ca3af', fontWeight: 600 }}>{r.normF}°</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center', background: '#f8fbff' }}>{degCell(r.el, r.normE)}</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center', background: '#fff8f8' }}>{r.bi ? degCell(r.er, r.normE) : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center', color: '#9ca3af', fontWeight: 600 }}>{r.normE}°</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center', background: '#f8fbff' }}>{gradeChip(r.ml)}</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center', background: '#fff8f8' }}>{r.bi ? gradeChip(r.mr) : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                                <td style={{ padding: '7px 8px', textAlign: 'center' }}>
                                  {(r.painL || r.painR)
                                    ? <span style={{ fontSize: 16 }} title={`L:${r.painL ? 'yes' : 'no'} R:${r.painR ? 'yes' : 'no'}`}>🔴</span>
                                    : <span style={{ fontSize: 16 }}>✅</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ marginTop: 10, fontSize: 11, color: '#9ca3af', display: 'flex', gap: 16 }}>
                        <span><span style={{ color: '#16a34a', fontWeight: 700 }}>■</span> ≥90% normal</span>
                        <span><span style={{ color: '#d97706', fontWeight: 700 }}>■</span> 60–89% normal</span>
                        <span><span style={{ color: '#dc2626', fontWeight: 700 }}>■</span> &lt;60% normal</span>
                        <span>🔴 Pain reported &nbsp; ✅ No pain</span>
                      </div>
                    </div>
                  )}

                  {/* ── No assessment yet ── */}
                  {assessRows.length === 0 && (
                    <div style={{ background: '#fff', borderRadius: 14, padding: '32px 24px', boxShadow: '0 2px 10px rgba(0,0,0,.07)', marginBottom: 22, textAlign: 'center', color: '#9ca3af' }}>
                      <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>No assessment recorded yet</div>
                      <div style={{ fontSize: 13, marginTop: 6 }}>Switch to the <strong>ROM Assessment</strong> or <strong>Muscle Strength</strong> tab to enter data.</div>
                    </div>
                  )}

                  {/* ── Billing quick summary ── */}
                  {visible && (
                    <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 10px rgba(0,0,0,.07)', marginBottom: 22 }}>
                      <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#1a1d23' }}>💳 Billing Summary</h4>
                      {(() => {
                        const tot = bills.reduce((s,b) => s+b.total,    0);
                        const rec = bills.reduce((s,b) => s+b.paid,     0);
                        const dis = bills.reduce((s,b) => s+b.discount, 0);
                        const formalBal = (profile?.previousbalancedetails || []).reduce((s, d) => s + Number(d.balance || 0), 0);
                        const billedOutstanding = (profile?.totalSoFar || 0) - (profile?.totalPaidSoFar || 0);
                        const unbilledFees = Math.max(0, (profile?.overallVisitCharges || 0) - (profile?.totalSoFar || 0));
                        const bal = Math.max(formalBal, billedOutstanding) + unbilledFees;
                        return (
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {[
                              { label: 'Total Billed',  value: `₹${tot}`, color: '#1976d2' },
                              { label: 'Discount',      value: `₹${dis}`, color: '#7b1fa2' },
                              { label: 'Received',      value: `₹${rec}`, color: '#2e7d32' },
                              { label: 'Balance Due',   value: `₹${bal}`, color: bal > 0 ? '#d32f2f' : '#2e7d32' },
                            ].map(item => (
                              <div key={item.label} style={{ flex: '1 1 120px', textAlign: 'center', padding: '12px 16px', background: item.color + '10', borderRadius: 10, border: `1.5px solid ${item.color}30` }}>
                                <div style={{ fontSize: 18, fontWeight: 800, color: item.color }}>{item.value}</div>
                                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginTop: 3 }}>{item.label}</div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                </div>
              );
            })()}
          </div>

          {/* ── Sessions tab ── */}
          <div style={{ display: pdTab === 'sessions' ? '' : 'none', marginTop: 16 }}>
            {/* Payment allocation summary cards */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{
                background: sessionCredit >= 0 ? '#f0fdf4' : '#fef2f2',
                border: '1.5px solid ' + (sessionCredit >= 0 ? '#4ade80' : '#f87171'),
                borderRadius: 10, padding: '10px 16px', flex: 1, minWidth: 140,
              }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>
                  {sessionCredit >= 0 ? 'Advance Remaining' : 'Outstanding Amount'}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: sessionCredit >= 0 ? '#16a34a' : '#dc2626' }}>
                  {sessionCredit >= 0 ? '+' : '-'}₹{Math.abs(sessionCredit).toLocaleString()}
                </div>
              </div>

              <div style={{ background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 10, padding: '10px 16px', flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Total Paid</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#d97706' }}>₹{paidSum.toLocaleString()}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{bills.length} bill{bills.length !== 1 ? 's' : ''}</div>
              </div>

              {coverageEndDt && (
                <div style={{ background: '#f5f3ff', border: '1.5px solid #a78bfa', borderRadius: 10, padding: '10px 16px', flex: 1, minWidth: 140 }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Sessions Paid Until</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#7c3aed' }}>
                    {coverageEndDt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
              )}
            </div>

            {/* Pending sessions notice */}
            {pendingCount > 0 && (
              <div style={{
                background: '#fef2f2', border: '1.5px solid #f87171',
                borderRadius: 10, padding: '12px 16px', marginBottom: 12,
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1.3 }}>⚠</span>
                <div>
                  <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 13, marginBottom: 3 }}>
                    {pendingCount} session{pendingCount !== 1 ? 's' : ''} pending payment
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
                    {pendingStartDt && (
                      <span>Starting from&nbsp;
                        <strong style={{ color: '#ef4444' }}>
                          {pendingStartDt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </strong>.&nbsp;
                      </span>
                    )}
                    Total outstanding:&nbsp;
                    <strong style={{ color: '#ef4444' }}>₹{Math.abs(Math.min(sessionCredit, 0)).toLocaleString()}</strong>.
                    &nbsp;Please contact the centre to settle your account.
                  </div>
                </div>
              </div>
            )}

            <div style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', boxShadow: '0 2px 10px rgba(0,0,0,.07)', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1d23', marginBottom: 10 }}>
                Your Therapy Sessions&nbsp;
                <span style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 10, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                  {sessions.length}
                </span>
              </div>
              <DataTable
                columns={pdSessionColumns}
                data={sessions}
                customStyles={lightTable}
                expandableRows
                expandableRowsComponent={PdSessionExpandRow}
                highlightOnHover
                dense
                pagination
                paginationPerPage={15}
                noDataComponent={<p style={{ color: '#9ca3af', padding: 16 }}>No therapy sessions found.</p>}
              />
            </div>
          </div>{/* end sessions tab */}

          {/* ── Bills tab ── */}
          <div style={{ display: pdTab === 'bills' ? '' : 'none', marginTop: 16 }}>
            {/* Bills summary strip */}
            {bills.length > 0 && (() => {
              const totalSum    = bills.reduce((s, b) => s + b.total,    0);
              const paidSumB    = bills.reduce((s, b) => s + b.paid,     0);
              const discountSum = bills.reduce((s, b) => s + b.discount, 0);
              const formalBal   = (profile?.previousbalancedetails || []).reduce((s, d) => s + Number(d.balance || 0), 0);
              const billedOutstanding = (profile?.totalSoFar || 0) - (profile?.totalPaidSoFar || 0);
              const unbilledFees = Math.max(0, (profile?.overallVisitCharges || 0) - (profile?.totalSoFar || 0));
              const balanceSum  = Math.max(formalBal, billedOutstanding) + unbilledFees;
              return (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                  {[
                    { label: 'Total Billed',  value: `₹${totalSum}`,    color: '#1976d2' },
                    { label: 'Discount',      value: `₹${discountSum}`, color: '#7c3aed' },
                    { label: 'Received',      value: `₹${paidSumB}`,   color: '#2e7d32' },
                    { label: 'Balance Due',   value: `₹${balanceSum}`,  color: balanceSum > 0 ? '#dc2626' : '#2e7d32' },
                  ].map(item => (
                    <div key={item.label} style={{ flex: '1 1 120px', textAlign: 'center', padding: '12px 16px', background: item.color + '10', borderRadius: 10, border: `1.5px solid ${item.color}30` }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: item.color }}>{item.value}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginTop: 3 }}>{item.label}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            <div style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', boxShadow: '0 2px 10px rgba(0,0,0,.07)', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1d23', marginBottom: 10 }}>Medical Yoga Therapy Centre</div>
              <DataTable
                columns={pdBillColumns}
                data={bills.filter(b => b.billorg === 'MedicalyogaTherapyCentre')}
                customStyles={lightTable}
                expandableRows
                expandableRowsComponent={PdBillExpandRow}
                highlightOnHover
                dense
                pagination
                paginationPerPage={10}
                noDataComponent={<p style={{ color: '#9ca3af', padding: 16 }}>No bills found for this centre.</p>}
              />
            </div>

            <div style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', boxShadow: '0 2px 10px rgba(0,0,0,.07)', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1d23', marginBottom: 10 }}>ReRoot Wellness</div>
              <DataTable
                columns={pdBillColumns}
                data={bills.filter(b => b.billorg === 'ReRoot')}
                customStyles={lightTable}
                expandableRows
                expandableRowsComponent={PdBillExpandRow}
                highlightOnHover
                dense
                pagination
                paginationPerPage={10}
                noDataComponent={<p style={{ color: '#9ca3af', padding: 16 }}>No bills found for ReRoot.</p>}
              />
            </div>

            {bills.length === 0 && !loading && (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>No billing records found for this patient.</div>
            )}
            {loading && <div style={{ marginTop: 20, color: '#555' }}>Fetching bills...</div>}
          </div>{/* end bills tab */}

          {/* ── ROM Assessment tab ── */}
          <div style={{ display: pdTab === 'rom' ? '' : 'none', marginTop: 20 }}>
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e3a5f', marginBottom: 4 }}>📐 ROM Progress Over Time</div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 14 }}>Range of motion (degrees) per body part — hover for values, toggle each card for table view</div>
              <AllRomPartsProgress romHistory={romHistory} loading={romHistLoading} mode="rom" />
            </div>
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 20, marginTop: 4 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 12 }}>📝 ROM Assessment Entry</div>
              <RomAssessmentPanel
                patientCard={profile.patientCard}
                patientName={profile.name}
                activeTab="rom"
                showTabBar={false}
              />
            </div>
          </div>

          {/* ── Muscle Strength tab ── */}
          <div style={{ display: pdTab === 'muscle' ? '' : 'none', marginTop: 20 }}>
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e3a5f', marginBottom: 4 }}>💪 Muscle Strength Progress Over Time</div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 14 }}>Muscle strength (MVC grade 0–5) per body part — hover for values, toggle each card for table view</div>
              <AllRomPartsProgress romHistory={romHistory} loading={romHistLoading} mode="muscle" />
            </div>
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 20, marginTop: 4 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 12 }}>📝 Muscle Strength Entry</div>
              <RomAssessmentPanel
                patientCard={profile.patientCard}
                patientName={profile.name}
                activeTab="muscle"
                showTabBar={false}
              />
            </div>
          </div>

          {/* ── AI Report tab ── */}
          <div style={{ display: pdTab === 'aiReport' ? '' : 'none', marginTop: 16 }}>
            {profile?.patientCard && <PatientAIReport patientCard={profile.patientCard} />}
          </div>
        </>
      )}{/* end profile?.patientCard */}
    </div>
  );
}
